import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, Calendar, Clock, Hash, Loader2, Sparkles, BookOpen,
    CheckCircle2, AlertTriangle, Briefcase, User, Layers, ChevronRight, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import CandidateTable from '@/components/schedule/CandidateTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { NativeSelect } from '@/components/ux/NativeSelect';
import { cn } from '@/lib/utils';

/**
 * Scheduling as a page rather than a dialog.
 *
 * The flow has four decisions — job description, candidate, questions, time —
 * and the candidate step alone can list eighty people. A modal cannot hold
 * that. The right column keeps a live summary of exactly what will be created,
 * so the whole decision is visible without scrolling back.
 *
 * Reads the same URL parameters the dialog did (templateId, candidateId,
 * processId, questionSetId), so every existing entry point keeps working.
 */

const LOADING_MESSAGES = [
    'Analysing the job description…',
    'Generating role-specific questions…',
    'Calibrating difficulty…',
    'Preparing the interview…',
    'Almost there…',
];

function toDateInputValue(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function toTimeInputValue(d) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** Default to the next hour, so the field is never pre-filled with a past time. */
function nextHour() {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d;
}
function combineDateTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const d = new Date(`${dateStr}T${timeStr}`);
    return Number.isNaN(d.getTime()) ? null : d;
}

function mapPreviewQuestion(q) {
    const id = q._id != null ? String(q._id) : (q.id != null ? String(q.id) : '');
    return {
        ...q,
        _id:       id || undefined,
        _included: true,
        _timeSecs: Number(q.timeLimitSeconds) > 0 ? Number(q.timeLimitSeconds) : 300,
    };
}

/** Send overrides only for exclusions or changed time limits — omitting means "use the set as-is". */
function buildQuestionOverrides(previewQuestions) {
    return previewQuestions
        .filter(q => q._id)
        .map(q => {
            const excluded    = q._included === false;
            const timeChanged = Number(q._timeSecs) > 0
                && Number(q._timeSecs) !== Number(q.timeLimitSeconds);
            if (!excluded && !timeChanged) return null;
            return {
                _id: String(q._id),
                ...(excluded ? { excluded: true } : {}),
                ...(timeChanged ? { timeLimitSeconds: Number(q._timeSecs) } : {}),
            };
        })
        .filter(Boolean);
}

/**
 * Compact page header for the form pages.
 *
 * The shared PageHeader carries mb-8, pb-6 and a rule — around 80px before any
 * field, which is right for a list page and wasteful on a form someone fills in
 * regularly. Back arrow and title sit on one line here.
 */
function FormHeader({ title, subtitle, onBack }) {
    return (
        <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                onClick={onBack} aria-label="Back">
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
                <h1 className="truncate text-base font-semibold tracking-tight text-foreground">{title}</h1>
                <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
            </div>
        </div>
    );
}

function Step({ n, title, subtitle, done, children }) {
    return (
        <section className="space-y-2">
            <div className="flex items-baseline gap-2">
                <span className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center self-center rounded-full text-[10px] font-bold',
                    done ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-primary/10 text-primary',
                )}>
                    {done ? <CheckCircle2 className="h-3 w-3" /> : n}
                </span>
                <h2 className="text-sm font-semibold text-foreground">{title}</h2>
                {subtitle && (
                    <span className="hidden min-w-0 truncate text-xs text-muted-foreground sm:inline">
                        — {subtitle}
                    </span>
                )}
            </div>
            {children}
        </section>
    );
}

export default function ScheduleInterviewNewPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const initialTemplateId  = params.get('templateId')    || '';
    const initialCandidateId = params.get('candidateId')   || '';
    const processId          = params.get('processId')     || '';
    const initialSetId       = params.get('questionSetId') || '';

    const [templates, setTemplates] = useState([]);
    const [passThreshold, setPassThreshold] = useState(70);

    const [templateId,  setTemplateId]  = useState(initialTemplateId);
    const [candidateId, setCandidateId] = useState(initialCandidateId);
    const [dateStr, setDateStr] = useState(() => toDateInputValue(nextHour()));
    const [timeStr, setTimeStr] = useState(() => toTimeInputValue(nextHour()));

    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);

    const [schedulable, setSchedulable] = useState(null);
    const [schedulableLoading, setSchedulableLoading] = useState(false);

    const [questionSource, setQuestionSource] = useState('dynamic');
    const [availableSets,  setAvailableSets]  = useState([]);
    const [setsLoading,    setSetsLoading]    = useState(false);
    const [selectedSetId,  setSelectedSetId]  = useState(initialSetId);
    const [previewQuestions, setPreviewQuestions] = useState([]);
    const [previewLoading,   setPreviewLoading]   = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Scheduling…');
    const [error, setError] = useState('');
    const msgTimer = useRef(null);

    useEffect(() => () => clearInterval(msgTimer.current), []);

    // ── Reference data ────────────────────────────────────────────────────────
    useEffect(() => {
        apiClient.getTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
        apiClient.getInterviewSettings()
            .then(r => setPassThreshold(r.data?.passThreshold ?? 70))
            .catch(() => {});
    }, []);

    // ── Candidates for the chosen job description ─────────────────────────────
    useEffect(() => {
        if (!templateId) { setCandidates([]); return; }
        setCandidatesLoading(true);
        apiClient.getCandidatesForTemplate(templateId)
            .then(r => setCandidates(r.data || []))
            .catch(() => setCandidates([]))
            .finally(() => setCandidatesLoading(false));
    }, [templateId]);

    // ── Which round is schedulable ────────────────────────────────────────────
    useEffect(() => {
        if (!templateId || !candidateId) { setSchedulable(null); return; }
        setSchedulableLoading(true);
        setSchedulable(prev => (prev ? { ...prev, schedulable: null } : null));
        apiClient.getSchedulableRound(templateId, candidateId, processId || undefined)
            .then(r => setSchedulable(r.data || null))
            .catch(err => setSchedulable({ schedulable: null, message: err.message }))
            .finally(() => setSchedulableLoading(false));
    }, [templateId, candidateId, processId]);

    // ── Question sets for this job description ────────────────────────────────
    useEffect(() => {
        if (!templateId) { setAvailableSets([]); return; }
        setSetsLoading(true);
        apiClient.getQuestionSets({ templateId })
            .then(r => setAvailableSets(r.data || []))
            .catch(() => setAvailableSets([]))
            .finally(() => setSetsLoading(false));
    }, [templateId]);

    // ── Preview the chosen set ────────────────────────────────────────────────
    useEffect(() => {
        if (questionSource !== 'set' || !selectedSetId) { setPreviewQuestions([]); return; }
        setPreviewLoading(true);
        apiClient.getQuestionSet(selectedSetId)
            .then(r => setPreviewQuestions((r.data?.questions || []).map(mapPreviewQuestion)))
            .catch(() => setPreviewQuestions([]))
            .finally(() => setPreviewLoading(false));
    }, [questionSource, selectedSetId]);

    // A set arriving by URL implies the set path.
    useEffect(() => { if (initialSetId) setQuestionSource('set'); }, [initialSetId]);

    // Changing job description invalidates everything downstream.
    const onTemplateChange = (id) => {
        setTemplateId(id);
        setCandidateId('');
        setSelectedSetId('');
        setQuestionSource('dynamic');
        setError('');
    };

    const round = schedulable?.schedulable || null;
    const usingSet = questionSource === 'set' && Boolean(selectedSetId);
    const includedCount = previewQuestions.filter(q => q._included !== false).length;

    /**
     * What the interview will actually contain.
     *
     * The round blueprint is only the default. Choosing a set overrides the
     * question count with the set's own — the server stores
     * `questionsOverride.length` — so showing the blueprint number here would
     * state something untrue about the interview being created.
     */
    const effective = useMemo(() => {
        if (!round) return null;
        if (usingSet && previewQuestions.length) {
            // Length comes from the set's own per-question limits, not the
            // blueprint — those are what the candidate is actually given. Summing
            // the included questions keeps the figure honest as they are excluded.
            const seconds = previewQuestions
                .filter(q => q._included !== false)
                .reduce((total, q) => total + (Number(q._timeSecs) || 0), 0);

            return {
                questions:  includedCount,
                duration:   Math.max(1, Math.ceil(seconds / 60)),
                fromSet:    true,
                overridden: includedCount !== round.numberOfQuestions,
                blueprintQuestions: round.numberOfQuestions,
                blueprintDuration:  round.durationMinutes,
            };
        }
        return {
            questions:  round.numberOfQuestions,
            duration:   round.durationMinutes,
            fromSet:    false,
            overridden: false,
        };
    }, [round, usingSet, previewQuestions, includedCount]);

    const selectedTemplate  = templates.find(t => t._id === templateId);
    const selectedCandidate = candidates.find(c => String(c.candidateId) === candidateId);
    const scheduledDate     = combineDateTime(dateStr, timeStr);

    const canSubmit = Boolean(templateId && candidateId && round && scheduledDate && !submitting)
        && (!usingSet || includedCount > 0);

    const submit = useCallback(async () => {
        setError('');
        const when = combineDateTime(dateStr, timeStr);
        if (!when)                 return setError('Choose a valid date and time.');
        if (when.getTime() < Date.now()) return setError('Interviews cannot be scheduled in the past.');
        if (usingSet && includedCount === 0) {
            return setError('Every question is excluded — include at least one.');
        }

        setSubmitting(true);
        if (usingSet) {
            setLoadingMessage('Scheduling interview…');
        } else {
            let i = 0;
            setLoadingMessage(LOADING_MESSAGES[0]);
            msgTimer.current = setInterval(() => {
                i = (i + 1) % LOADING_MESSAGES.length;
                setLoadingMessage(LOADING_MESSAGES[i]);
            }, 2500);
        }

        try {
            const payload = {
                templateId:  String(templateId),
                candidateId: String(candidateId),
                scheduledAt: when.toISOString(),
            };
            if (usingSet) {
                payload.questionSetId = String(selectedSetId);
                const overrides = buildQuestionOverrides(previewQuestions);
                if (overrides.length) payload.questionOverrides = overrides;
            }

            await apiClient.createSession(payload);
            toast.success(usingSet ? 'Interview scheduled with question set.' : 'Interview scheduled.');
            navigate('/admin/schedule');
        } catch (err) {
            setError(err.message || err.data?.message || 'Failed to schedule interview.');
        } finally {
            clearInterval(msgTimer.current);
            setSubmitting(false);
        }
    }, [templateId, candidateId, dateStr, timeStr, usingSet, selectedSetId, previewQuestions, includedCount, navigate]);

    return (
        <div className=" max-w-[1400px] space-y-4">
            <FormHeader
                title="Schedule an interview"
                subtitle="Pick the role and candidate — the round is worked out from their progress."
                onBack={() => navigate('/admin/schedule')}
            />

            {error && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}
                </div>
            )}

            <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
                {/* ── Steps ─────────────────────────────────────────────── */}
                <div className="min-w-0 flex-1 space-y-4">

                    <Step n={1} title="Job description" done={Boolean(templateId)}
                        >
                        <NativeSelect id="tpl" value={templateId} onChange={e => onTemplateChange(e.target.value)}
                            className="space-y-0 [&_label]:hidden">
                            <option value="">Select a job description…</option>
                            {templates.map(t => (
                                <option key={t._id} value={t._id}>
                                    {t.title}{t.department ? ` · ${t.department}` : ''}
                                </option>
                            ))}
                        </NativeSelect>
                        {selectedTemplate && !selectedTemplate.interviewRounds?.length && (
                            <p className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                This job description has no interview rounds configured. Add them before scheduling.
                            </p>
                        )}
                    </Step>

                    <Step n={2} title="Candidate" done={Boolean(candidateId)}
                        subtitle="evaluated and accepted only">
                        {templateId ? (
                            <CandidateTable
                                candidates={candidates}
                                loading={candidatesLoading}
                                value={candidateId}
                                onChange={setCandidateId}
                                passThreshold={selectedTemplate?.passThreshold ?? passThreshold}
                                templateId={templateId}
                            />
                        ) : (
                            <p className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                                Choose a job description first.
                            </p>
                        )}
                    </Step>

                    <Step n={3} title="Round" done={Boolean(round)}
                        subtitle="Worked out from the candidate's progress — you do not pick it.">
                        {schedulableLoading ? (
                            <p className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />Checking their progress…
                            </p>
                        ) : round ? (
                            <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="secondary">{round.roundName}</Badge>
                                    {schedulable?.isRetake && (
                                        <Badge variant="outline">Retake · attempt {schedulable.attemptNumber}</Badge>
                                    )}
                                    {!schedulable?.isRetake && schedulable?.attemptNumber > 1 && (
                                        <Badge variant="outline">Attempt {schedulable.attemptNumber}</Badge>
                                    )}
                                </div>
                                <div className="grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
                                    <span><strong className="text-foreground">Type:</strong> {round.interviewTypeName}</span>
                                    <span><strong className="text-foreground">Difficulty:</strong> {round.difficultyName}</span>
                                    <span className="flex items-center gap-1">
                                        <Hash className="h-3.5 w-3.5" />
                                        <strong className="text-foreground">{effective?.questions}</strong> questions
                                        {effective?.overridden && (
                                            <span className="text-[10px] text-muted-foreground/70">
                                                (job description says {effective.blueprintQuestions})
                                            </span>
                                        )}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Clock className="h-3.5 w-3.5" />
                                        <strong className="text-foreground">{effective?.duration}</strong> min
                                        {effective?.fromSet && effective.duration !== effective.blueprintDuration && (
                                            <span className="text-[10px] text-muted-foreground/70">
                                                (job description says {effective.blueprintDuration})
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ) : candidateId ? (
                            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200">
                                {schedulable?.message || 'This candidate cannot be scheduled right now.'}
                            </p>
                        ) : (
                            <p className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                                Pick a candidate to see which round is next.
                            </p>
                        )}
                    </Step>

                    <Step n={4} title="Questions" done={Boolean(round)}
                        subtitle="saved set or AI generated">
                        <div className="space-y-3">
                            <div className="grid gap-2 sm:grid-cols-2">
                                {[
                                    { key: 'dynamic', icon: Sparkles, title: 'Generate with AI',
                                      desc: 'Fresh questions from the job description at schedule time.' },
                                    { key: 'set', icon: BookOpen, title: 'Use a question set',
                                      desc: availableSets.length ? `${availableSets.length} available for this role.` : 'None saved for this role yet.' },
                                ].map(opt => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => setQuestionSource(opt.key)}
                                        disabled={opt.key === 'set' && !availableSets.length && !setsLoading}
                                        title={opt.key === 'set' && !availableSets.length && !setsLoading
                                            ? 'No question sets saved for this role yet' : undefined}
                                        className={cn(
                                            'rounded-xl border p-3 text-left transition-colors',
                                            questionSource === opt.key
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-primary/40',
                                            opt.key === 'set' && !availableSets.length && !setsLoading && 'cursor-not-allowed opacity-50',
                                        )}
                                    >
                                        <opt.icon className={cn('h-4 w-4', questionSource === opt.key ? 'text-primary' : 'text-muted-foreground')} />
                                        <p className="mt-1.5 text-xs font-semibold text-foreground">{opt.title}</p>
                                        <p className="text-[11px] leading-relaxed text-muted-foreground">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>

                            {/* A disabled tile with no route forward is a dead end —
                                offer to create a set for this role. */}
                            {templateId && !setsLoading && !availableSets.length && (
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2.5">
                                    <p className="text-xs text-muted-foreground">
                                        No question sets saved for this job description yet.
                                    </p>
                                    <Button
                                        type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-[11px]"
                                        onClick={() => navigate(`/admin/question-sets?templateId=${encodeURIComponent(templateId)}&create=1`)}
                                    >
                                        <Plus className="h-3 w-3" /> Create a question set
                                    </Button>
                                </div>
                            )}

                            {questionSource === 'set' && (
                                <div className="space-y-2">
                                    <NativeSelect id="qset" value={selectedSetId} onChange={e => setSelectedSetId(e.target.value)}
                                        className="space-y-0 [&_label]:hidden [&_select]:h-9 [&_select]:text-xs">
                                        <option value="">Select a question set…</option>
                                        {availableSets.map(s => (
                                            <option key={s._id} value={s._id}>
                                                {s.name} — {s.numberOfQuestions ?? '?'} questions
                                            </option>
                                        ))}
                                    </NativeSelect>

                                    {previewLoading && (
                                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading questions…
                                        </p>
                                    )}

                                    {previewQuestions.length > 0 && (
                                        <div className="rounded-xl border border-border">
                                            <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
                                                <p className="text-[11px] font-semibold text-foreground">
                                                    {includedCount} of {previewQuestions.length} included
                                                </p>
                                                <p className="text-[10px] text-muted-foreground">Untick to exclude</p>
                                            </div>
                                            <div className="max-h-56 space-y-1 overflow-y-auto p-2">
                                                {previewQuestions.map((q, i) => (
                                                    <label key={q._id || i}
                                                        className={cn('flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50',
                                                            q._included === false && 'opacity-50')}>
                                                        <input
                                                            type="checkbox"
                                                            checked={q._included !== false}
                                                            onChange={() => setPreviewQuestions(prev => prev.map((x, xi) =>
                                                                xi === i ? { ...x, _included: x._included === false } : x))}
                                                            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-input"
                                                        />
                                                        <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-muted-foreground">
                                                            {q.questionText}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Step>

                    <Step n={5} title="Date and time" done={Boolean(scheduledDate)}>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label htmlFor="d">Date</Label>
                                <Input id="d" type="date" value={dateStr} min={toDateInputValue(new Date())}
                                    onChange={e => setDateStr(e.target.value)} className="h-10" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="t">Time</Label>
                                <Input id="t" type="time" value={timeStr}
                                    onChange={e => setTimeStr(e.target.value)} className="h-10" />
                            </div>
                        </div>
                    </Step>
                </div>

                {/* ── Summary ───────────────────────────────────────────── */}
                <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[21rem]">
                    <div className="rounded-xl border border-border bg-card p-3.5">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Interview summary
                        </p>

                        <dl className="space-y-2 text-xs">
                            {[
                                { icon: Briefcase, label: 'Role',      value: selectedTemplate?.title },
                                { icon: User,      label: 'Candidate', value: selectedCandidate
                                    ? `${selectedCandidate.firstName || ''} ${selectedCandidate.lastName || ''}`.trim() || selectedCandidate.email
                                    : null },
                                { icon: Layers,    label: 'Round',     value: round?.roundName },
                                { icon: Hash,      label: 'Questions', value: effective ? `${effective.questions}` : null },
                                { icon: Clock, label: 'Duration', value: effective
                                    ? `${effective.duration} min${effective.fromSet ? ' (from set)' : ''}` : null },
                                { icon: Sparkles,  label: 'Source',    value: usingSet
                                    ? availableSets.find(s => s._id === selectedSetId)?.name || 'Question set'
                                    : 'AI generated' },
                                { icon: Calendar,  label: 'When',      value: scheduledDate
                                    ? scheduledDate.toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
                                    : null },
                            ].map(row => (
                                <div key={row.label} className="flex items-start gap-2">
                                    <row.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{row.label}</dt>
                                        <dd className={cn('truncate font-medium', row.value ? 'text-foreground' : 'text-muted-foreground/50')}>
                                            {row.value || 'Not set'}
                                        </dd>
                                    </div>
                                </div>
                            ))}
                        </dl>

                        <Button className="mt-4 w-full gap-2" disabled={!canSubmit} onClick={submit}>
                            {submitting
                                ? <><Loader2 className="h-4 w-4 animate-spin" />{loadingMessage}</>
                                : <>Schedule interview<ChevronRight className="h-4 w-4" /></>}
                        </Button>

                        {!submitting && !canSubmit && (
                            <p className="mt-2 text-center text-[11px] text-muted-foreground">
                                {!templateId ? 'Choose a job description'
                                    : !candidateId ? 'Choose a candidate'
                                    : !round ? 'No round can be scheduled yet'
                                    : usingSet && includedCount === 0 ? 'Include at least one question'
                                    : 'Choose a date and time'}
                            </p>
                        )}

                        {!submitting && canSubmit && (
                            <p className="mt-2 text-center text-[11px] text-muted-foreground">
                                The candidate is emailed their login details.
                            </p>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}
